import 'core-js/full'; // for set operations polyfill
import { Redirect } from 'react-router-dom';

import Overview from './containers/Overview/Overview';
import RetailerAPI from './containers/RetailerAPI/RetailerAPI';
import Support from './containers/Support/Support';
import AllDevices from './containers/AllDevices/AllDevices';
import RetailerReporting from './containers/RetailerReporting/RetailerReporting';
import UserManagement from './containers/UserManagement/UserManagement';
import NoAccountFunctionality from './containers/ErrorStates/NoAccountFunctionality';
import CustomerDetails from './containers/DevicesDetail/CustomerDetails';
import GatewaysPage from './containers/Gateways/GatewaysPage';
import GatewaysOverview from './containers/Gateways/GatewaysOverview';
import FleetHealthPage from './containers/Gateways/FleetHealthPage';
import GatewayPage from './containers/Gateways/GatewayPage';
import SearchCustomers from './containers/DevicesDetail/SearchCustomers';
import BatteriesList from './containers/Batteries/BatteriesList';
import BatteryPage from './containers/Batteries/BatteryPage';
import Announcements from './containers/Announcements';
import VbattDevicePage from './containers/VirtualBatteries/Device';
import VbattDeviceHealth from './containers/VbattDevices/VbattDeviceHealth';
import VBattDeviceAlerts from './containers/VbattDevices/VBattDeviceAlerts';
import ModeScheduler from './containers/ModeScheduler/ModeScheduler';
import ProductRoadmap from './containers/Roadmap/ProductRoadmap';
import NewPortalFacilities from './containers/NewPortal/NewPortalFacilities';

// Link out to the new portal. Same role as the pages it sits between, so it
// opens no door that was not already open.

const RedirectToBatteries = () => <Redirect to="/batteries" />;
const PORTAL_V2_FACILITIES_ROUTE = {
    path: '/facilities',
    component: NewPortalFacilities,
    allowedRoles: {
        retailer: new Set(['customer_data_read']),
        brp: new Set([]),
    },
};

/**
 * Which retailers are invited to the new portal.
 *
 * `all` opens it to every retailer; a comma-separated list of legacy retailer
 * ints opens it to exactly those; empty or unset opens it to nobody. Reading
 * an unset value as "nobody" rather than "everybody" is the point — a host
 * configured without a list is a rollout half-done, and the failure that
 * matters is showing an unfinished page to a tenant nobody chose.
 *
 * This is a rollout control, NOT an access control. The list ships inside a
 * public bundle and anyone can open the new portal's URL directly. What keeps
 * a tenant out of another's data is the new portal's own API — `retailer:read`
 * plus a signed tenant claim, with cross-tenant reads answering 404.
 */
function invitedToPortalV2(retailerId) {
    if (!process.env.REACT_APP_PORTAL_V2_URL) {
        return false;
    }
    const invited = (process.env.REACT_APP_PORTAL_V2_RETAILERS || '').trim();
    if (invited === '') {
        return false;
    }
    if (invited === 'all') {
        return true;
    }
    // String-compared: the claim arrives as a number, the env var as text, and
    // `undefined` (a session with no retailer) must never match an entry.
    return invited.split(',').some(entry => entry.trim() !== '' && entry.trim() === String(retailerId));
}

export default function getRoutes(availableRoles, retailerId) {
    // Appended rather than spliced in: `App.js` redirects "/" to
    // availableRoutes[0], so anything inserted earlier could quietly become
    // the landing page for a whole tenant.
    const candidates = invitedToPortalV2(retailerId) ? [...ROUTES, PORTAL_V2_FACILITIES_ROUTE] : ROUTES;

    const { availableRoutes } = candidates.reduce(
        (routes, route) => {
            if (routes.routesSeen.has(route.path)) {
                throw new Error(`duplicate route registered: ${route.path}`);
            }

            const isAllowed = hasAtLeastOneMatchingRole(availableRoles, route.allowedRoles);
            if (isAllowed) {
                routes.availableRoutes.push(route);
                routes.routesSeen.add(route.path);
            }

            return routes;
        },
        { routesSeen: new Set(), availableRoutes: [] }
    );

    if (availableRoutes.length < 1) {
        // Catch-all landing page, only shown if the account has no matching roles
        availableRoutes.push({
            path: '/no-account-functionality',
            component: NoAccountFunctionality,
        });

        console.error(
            `no known routes available for account with ` +
                `retailer role(s): '${[...availableRoles.retailer]}' ` +
                `or brp role(s): '${[...availableRoles.brp]}'`
        );
    }

    return availableRoutes;
}

// RE `allowedRoles`:
// Currently a route is able to specify per-realm roles that should grant an account
// access. If an account has _at least_ one role (for the matching realm), then
// that is considered sufficient. This does mean that we cannot currently express
// more sophisticated matches, like `retailer.reporting` && `retailer.customer_data_read`
const ROUTES = [
    {
        path: '/devices',
        component: Overview,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/gateways',
        component: GatewaysOverview,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        // Must precede /gateways/:deviceId so ":deviceId" doesn't capture "health".
        path: '/gateways/health',
        component: FleetHealthPage,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/gateways/:deviceId',
        component: GatewayPage,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/devicesdetail/:id/gateways',
        component: GatewaysPage,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/devicesdetail/:id',
        component: CustomerDetails,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/devicesdetail',
        component: SearchCustomers,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/retailer-api',
        component: RetailerAPI,
        allowedRoles: {
            retailer: new Set(['api_management']),
            brp: new Set([]),
        },
    },
    {
        path: '/support',
        component: Support,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/all-devices',
        component: AllDevices,
        allowedRoles: {
            retailer: new Set(['customer_data_read']),
            brp: new Set([]),
        },
    },
    {
        path: '/retailer-reporting',
        component: RetailerReporting,
        allowedRoles: {
            retailer: new Set(['reporting']),
            brp: new Set([]),
        },
    },
    {
        path: '/user-management',
        component: UserManagement,
        allowedRoles: {
            retailer: new Set(['user_management']),
            brp: new Set([]),
        },
    },
    {
        // The old per-device list; the battery pages at /batteries replace it. Kept as a
        // redirect so bookmarks land there. The device pages below stay, linked from the
        // battery page's devices table.
        path: '/vbatt-devices',
        component: RedirectToBatteries,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write', 'api_management', 'reporting', 'user_management']),
            brp: new Set(['battery_manage']),
        },
    },
    {
        path: '/vbatt-devices/:id',
        component: VbattDevicePage,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write', 'api_management', 'reporting', 'user_management']),
            brp: new Set(['battery_manage']),
        },
    },
    {
        path: '/vbatt-devices/:id/health',
        component: VbattDeviceHealth,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write', 'api_management', 'reporting', 'user_management']),
            brp: new Set(['battery_manage']),
        },
    },
    {
        path: '/vbatt-devices/:id/alerts',
        component: VBattDeviceAlerts,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write', 'api_management', 'reporting', 'user_management']),
            brp: new Set(['battery_manage']),
        },
    },
    {
        path: '/devices/:id/mode-scheduler',
        component: ModeScheduler,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write']),
            brp: new Set([]),
        },
    },
    {
        // The retailer's virtual batteries, read over the retailer realm. Same roles as
        // /vbatt-devices; the BRP realm has no view here any more.
        path: '/batteries',
        component: BatteriesList,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write', 'api_management', 'reporting', 'user_management']),
            brp: new Set([]),
        },
    },
    {
        path: '/batteries/:id',
        component: BatteryPage,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'customer_data_write', 'api_management', 'reporting', 'user_management']),
            brp: new Set([]),
        },
    },
    {
        path: '/announcements',
        component: Announcements,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'reporting', 'user_management', 'api_management']),
            brp: new Set(['battery_read']),
        },
    },
    {
        path: '/product-roadmap',
        component: ProductRoadmap,
        allowedRoles: {
            retailer: new Set(['customer_data_read', 'reporting', 'user_management', 'api_management']),
            brp: new Set(['battery_read']),
        },
    },
];

function hasAtLeastOneMatchingRole(availableRoles, allowedRoles) {
    const matchingRetailerRoles = availableRoles.retailer.intersection(allowedRoles.retailer);
    const matchingBrpRoles = availableRoles.brp.intersection(allowedRoles.brp);
    // comparing the role matches per-realm here. If either realm has a match then return true.
    return matchingRetailerRoles.size > 0 || matchingBrpRoles.size > 0;
}
